import { useEffect, useState } from 'react';
import { Check } from '@/components/icons';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchPortalContext,
  submitPortalQuote,
  type PortalContext,
  type PortalSubmitPayload,
} from '@/api/vendorPortal';
import { formatDate } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import logoLight from '@/assets/logo-light.svg';

interface LineState {
  rfqItemId: number;
  itemName: string;
  qty: string;
  unit: string;
  unitPrice: string;
  deliveryDays: string;
  remarks: string;
}

export function VendorPortalPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [lines, setLines] = useState<LineState[]>([]);
  const [validUntil, setValidUntil] = useState('');
  const [paymentTerms] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery<PortalContext, Error>({
    queryKey: ['portal', token],
    queryFn: () => fetchPortalContext(token),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (!query.data) return;
    setLines(
      query.data.rfq.items.map((it) => ({
        rfqItemId: it.id,
        itemName: it.itemName,
        qty: String(it.qty),
        unit: it.unit,
        unitPrice: '',
        deliveryDays: '',
        remarks: '',
      })),
    );
  }, [query.data]);

  const submitMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error('Token not resolved');
      const payload: PortalSubmitPayload = {
        vendorId: query.data.vendor.id,
        validUntil: validUntil || undefined,
        paymentTerms: paymentTerms.trim() || undefined,
        remarks: remarks.trim() || undefined,
        items: lines
          .filter((l) => Number(l.unitPrice) > 0)
          .map((l) => ({
            rfqItemId: l.rfqItemId,
            unitPrice: Number(l.unitPrice),
            deliveryDays: l.deliveryDays ? Number(l.deliveryDays) : undefined,
            remarks: l.remarks.trim() || undefined,
          })),
      };
      return submitPortalQuote(token, payload);
    },
    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },
    onError: (e: unknown) => {
      setError(apiError(e, 'Submit failed'));
    },
  });

  function update(idx: number, patch: Partial<LineState>) {
    setLines((s) => s.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  if (query.isLoading) {
    return <PortalShell><div className="py-12 text-center" style={{ color: 'var(--text-muted)' }}>Loading…</div></PortalShell>;
  }
  if (query.isError) {
    return (
      <PortalShell>
        <div className="rounded-xl border-l-4 border-red-500 p-6" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>
          <div className="font-bold">Link not valid</div>
          <div className="mt-1 text-sm">
            This vendor link has expired, already been used, or is invalid. Please contact GRHAMOY
            purchase team for a fresh link.
          </div>
        </div>
      </PortalShell>
    );
  }
  if (!query.data) return null;
  if (submitted) {
    return (
      <PortalShell>
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full text-3xl" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
            <Check size={32} />
          </div>
          <div className="text-2xl font-extrabold" style={{ color: 'var(--text-primary)' }}>Quote Submitted Successfully!</div>
          <div className="mx-auto mt-3 max-w-md text-sm" style={{ color: 'var(--text-secondary)' }}>
            Your quotation for <strong>{query.data.rfq.rfqNo}</strong> has been received by GRHAMOY ERP. Our
            purchase team will review and get back to you shortly.
          </div>
          <div className="mt-4 text-sm font-bold text-[#FF8C00]">
            Thank you for your prompt response.
          </div>
        </div>
      </PortalShell>
    );
  }

  const rfq = query.data.rfq;
  const vendor = query.data.vendor;

  return (
    <PortalShell>
      {/* Header card */}
      <div
        className="mb-6 rounded-2xl p-6 text-white shadow-lg"
        style={{ background: 'linear-gradient(135deg, #FF8C00, #e07b00)' }}
      >
        <img
          src={logoLight}
          alt="grhamoy"
          style={{ height: '72px', width: 'auto', maxWidth: '220px', objectFit: 'contain', marginBottom: '6px' }}
        />
        <div className="text-sm font-bold opacity-90">Gauvin&apos;s Home Pvt Ltd</div>
        <div className="mt-1 text-xs opacity-80">Vendor Quote Submission Portal</div>
      </div>

      {/* RFQ details */}
      <PortalCard>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-[#FF8C00] px-3 py-1 text-xs font-bold text-white">RFQ</span>
            <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{rfq.rfqNo}</span>
          </div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Confidential — Vendor Use Only</div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <PortalField label="RFQ Date">{formatDate(rfq.rfqDate)}</PortalField>
          <PortalField label="Submission Deadline">
            <span className="font-bold text-[#e07b00]">{formatDate(rfq.deadline ?? '')}</span>
          </PortalField>
          <PortalField label="Vendor">{vendor.name}</PortalField>
          <PortalField label="Contact">{vendor.email ?? vendor.mobile ?? '—'}</PortalField>
        </div>
        <div className="mt-4 rounded-md border-l-4 border-[#FF8C00] px-3 py-2.5 text-xs" style={{ background: 'var(--input-bg)', color: 'var(--text-secondary)' }}>
          Please submit competitive rates for all items listed below. Ensure prices include
          applicable GST. Submit before the deadline.
        </div>
      </PortalCard>

      {/* Quote form */}
      <PortalCard>
        <div className="mb-3 text-base font-bold" style={{ color: 'var(--text-primary)' }}>Submit Your Quotation</div>
        {error && (
          <div className="mb-3 rounded-md border border-red-500/30 px-3 py-2 text-sm" style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>
            {error}
          </div>
        )}
        {/*
          Compact padding + text-[11px] so all seven columns fit comfortably
          without a horizontal scroll bar. The table stretches to the card
          width; each column has its own width budget below.
        */}
        <div>
          <table className="w-full table-fixed border-collapse text-[11px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide" style={{ background: 'var(--input-bg)', color: 'var(--text-muted)' }}>
                <th className="px-1.5 py-1.5 text-left" style={{ width: 28 }}>#</th>
                <th className="px-1.5 py-1.5 text-left" style={{ minWidth: 160 }}>Item Name</th>
                <th className="px-1.5 py-1.5 text-right" style={{ width: 44 }}>Qty</th>
                <th className="px-1.5 py-1.5 text-left" style={{ width: 44 }}>Unit</th>
                <th className="px-1.5 py-1.5 text-right" style={{ width: 90 }}>Price (₹)</th>
                <th className="px-1.5 py-1.5 text-right" style={{ width: 70 }}>Days</th>
                <th className="px-1.5 py-1.5 text-left" style={{ minWidth: 140 }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l.rfqItemId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td className="px-1.5 py-1.5" style={{ color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td className="px-1.5 py-1.5 font-bold" style={{ color: 'var(--text-primary)' }}>{l.itemName}</td>
                  <td className="px-1.5 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>{l.qty}</td>
                  <td className="px-1.5 py-1.5" style={{ color: 'var(--text-secondary)' }}>{l.unit}</td>
                  <td className="px-1.5 py-1.5">
                    <PortalInput
                      type="number"
                      step="0.01"
                      value={l.unitPrice}
                      onChange={(e) => update(idx, { unitPrice: e.target.value })}
                      placeholder="0.00"
                      style={{ textAlign: 'right', fontSize: '11px', padding: '4px 6px' }}
                    />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <PortalInput
                      type="number"
                      value={l.deliveryDays}
                      onChange={(e) => update(idx, { deliveryDays: e.target.value })}
                      placeholder="Days"
                      style={{ textAlign: 'right', fontSize: '11px', padding: '4px 6px' }}
                    />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <PortalInput
                      value={l.remarks}
                      onChange={(e) => update(idx, { remarks: e.target.value })}
                      placeholder="Optional"
                      style={{ fontSize: '11px', padding: '4px 6px' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Quote Valid Until
            </label>
            <PortalInput
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Terms &amp; Conditions / Special Notes
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder="Any special conditions, discounts, packing charges…"
              className="mt-1 w-full rounded-md px-3 py-2 text-sm outline-none"
              style={{
                border: '1px solid var(--border-color)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#FF8C00')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
            />
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
            className="rounded-md bg-[#FF8C00] px-6 py-2.5 text-sm font-bold text-white shadow hover:bg-[#e07b00] disabled:opacity-60"
          >
            {submitMutation.isPending ? 'Submitting…' : 'Submit Quotation'}
          </button>
        </div>
      </PortalCard>

      <div className="py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
        Powered by GRHAMOY ERP · Gauvin&apos;s Home Pvt Ltd, Haridwar · info@gauvinhome.com
      </div>
    </PortalShell>
  );
}

function PortalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen overflow-y-auto" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="mx-auto max-w-3xl p-6">{children}</div>
    </div>
  );
}

function PortalCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-5 rounded-xl p-6" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>{children}</div>
  );
}

function PortalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="mt-0.5 font-semibold" style={{ color: 'var(--text-secondary)' }}>{children}</div>
    </div>
  );
}

function PortalInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className, style, ...rest } = props;
  return (
    <input
      {...rest}
      className={`w-full rounded-md px-2.5 py-1.5 text-sm outline-none ${className ?? ''}`}
      style={{
        border: '1px solid var(--border-color)',
        background: 'var(--input-bg)',
        color: 'var(--text-primary)',
        ...style,
      }}
      onFocus={(e) => { (e.currentTarget.style.borderColor = '#FF8C00'); props.onFocus?.(e); }}
      onBlur={(e) => { (e.currentTarget.style.borderColor = 'var(--border-color)'); props.onBlur?.(e); }}
    />
  );
}
